import axios from "axios";
import type {
  AddLinePayload,
  CreateInvoicePayload,
  CreateRefundPayload,
  Invoice,
  InvoiceEvent,
  InvoiceLineItem,
  InvoiceWithLines,
  PaymentReceipt,
  ReceiptListQuery,
  RecordPaymentPayload,
  Refund,
  RefundablePayment,
  ReverseLotAction,
  ReverseRefundPayload,
} from "@/interfaces/invoices";
import type { RecordPayoutPayload } from "@/interfaces/purchases";
import { apiClient } from "@/lib/apiClient";

/** Rethrows the server's error message (not a bare axios error) so the UI can show it. */
function rethrowApiError(error: unknown, fallback: string): never {
  if (axios.isAxiosError(error) && error.response?.data?.error) {
    throw new Error(error.response.data.error as string);
  }
  throw error instanceof Error ? error : new Error(fallback);
}

const base = (groupId: number) => `/admin/groups/${groupId}/invoices`;

/** Server-paginated list query (shared by the group + finance invoice blotters). */
export interface InvoiceListQuery {
  status?: string; // comma-separated status names (pending,overdue,…)
  orgId?: number;
  groupId?: number;
  createdBy?: string; // comma-separated user ids
  search?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  page?: number;
  limit?: number;
}

/** An option for the Created-by filter. */
export interface InvoiceCreator {
  id: number;
  name: string | null;
}

/** Distinct invoice creators for a group (Created-by filter options). */
export async function fetchGroupInvoiceCreators(groupId: number): Promise<InvoiceCreator[]> {
  const { data } = await apiClient.get<{ success: boolean; data: InvoiceCreator[] }>(
    `${base(groupId)}/creators`,
  );
  return data.data;
}

/** Distinct invoice creators across all orgs (Finance Created-by filter options). */
export async function fetchFinanceInvoiceCreators(): Promise<InvoiceCreator[]> {
  const { data } = await apiClient.get<{ success: boolean; data: InvoiceCreator[] }>(
    "/admin/finance/invoices/creators",
  );
  return data.data;
}

export interface InvoiceListResponse {
  data: Invoice[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ReceiptListResponse {
  data: PaymentReceipt[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/** Drops undefined/empty params so the querystring stays clean. */
function cleanQuery(q: object): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === "") continue;
    out[k] = String(v);
  }
  return out;
}

/** The group's invoices — server-paginated. */
export async function fetchInvoices(
  groupId: number,
  query: InvoiceListQuery = {},
): Promise<InvoiceListResponse> {
  const { data } = await apiClient.get<{ success: boolean } & InvoiceListResponse>(base(groupId), {
    params: cleanQuery(query),
  });
  return data;
}

/** The invoice's PDF as a Blob (proxied same-origin from Stripe, auth via apiClient). */
export async function fetchInvoicePdf(groupId: number, invoiceId: number): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(`${base(groupId)}/${invoiceId}/pdf`, {
    responseType: "blob",
  });
  return data;
}

/** One invoice with its line items. */
export async function fetchInvoice(groupId: number, invoiceId: number): Promise<InvoiceWithLines> {
  const { data } = await apiClient.get<{ success: boolean; data: InvoiceWithLines }>(
    `${base(groupId)}/${invoiceId}`,
  );
  return data.data;
}

/** Creates an empty draft (header only); returns its id + Stripe id. */
export async function createInvoice(
  groupId: number,
  payload: CreateInvoicePayload,
): Promise<{ invoiceId: number; status: number; stripeInvoiceId: string | null }> {
  try {
    const { data } = await apiClient.post<{
      success: boolean;
      data: { invoiceId: number; status: number; stripeInvoiceId: string | null };
    }>(base(groupId), payload);
    return data.data;
  } catch (error) {
    rethrowApiError(error, "Couldn't create the invoice.");
  }
}

/** Draft header edits ("Save draft" + Bill-To change). */
export interface UpdateInvoicePayload {
  description?: string | null;
  expectedPaymentMethod?: "ach" | "wire" | "card" | "check";
  expectedPaymentChannel?: "stripe" | "direct_to_bank";
  daysUntilDue?: number | null;
  dueDate?: string | null;
  expectedReceiptDate?: string | null;
  paymentReferenceId?: string | null;
  billingAddressId?: number | null;
}

export async function updateInvoice(
  groupId: number,
  invoiceId: number,
  patch: UpdateInvoicePayload,
): Promise<void> {
  try {
    await apiClient.patch(`${base(groupId)}/${invoiceId}`, patch);
  } catch (error) {
    rethrowApiError(error, "Couldn't save the draft.");
  }
}

/** Adds one line to a draft; returns the created line. */
export async function addInvoiceLine(
  groupId: number,
  invoiceId: number,
  line: AddLinePayload,
): Promise<InvoiceLineItem> {
  try {
    const { data } = await apiClient.post<{ success: boolean; data: InvoiceLineItem }>(
      `${base(groupId)}/${invoiceId}/line-items`,
      line,
    );
    return data.data;
  } catch (error) {
    rethrowApiError(error, "Couldn't add the line.");
  }
}

/** Removes a line from a draft. */
export async function removeInvoiceLine(
  groupId: number,
  invoiceId: number,
  lineId: number,
): Promise<void> {
  try {
    await apiClient.delete(`${base(groupId)}/${invoiceId}/line-items/${lineId}`);
  } catch (error) {
    rethrowApiError(error, "Couldn't remove the line.");
  }
}

/** Finalizes (issues) a draft — Stripe emails the hosted link. */
export async function finalizeInvoice(
  groupId: number,
  invoiceId: number,
): Promise<{ invoiceId: number; status: number; invoiceNumber: string | null }> {
  try {
    const { data } = await apiClient.post<{
      success: boolean;
      data: { invoiceId: number; status: number; invoiceNumber: string | null };
    }>(`${base(groupId)}/${invoiceId}/finalize`);
    return data.data;
  } catch (error) {
    rethrowApiError(error, "Couldn't finalize the invoice.");
  }
}

/** Cancels a draft or pending invoice. */
export async function cancelInvoice(
  groupId: number,
  invoiceId: number,
  reason?: string,
): Promise<void> {
  try {
    await apiClient.post(`${base(groupId)}/${invoiceId}/cancel`, { reason });
  } catch (error) {
    rethrowApiError(error, "Couldn't cancel the invoice.");
  }
}

/** Sales pre-provision: release PROVISIONAL credits before payment (a reason is required). */
export async function provisionInvoice(
  groupId: number,
  invoiceId: number,
  reason: string,
): Promise<void> {
  try {
    await apiClient.post(`${base(groupId)}/${invoiceId}/provision`, { reason });
  } catch (error) {
    rethrowApiError(error, "Couldn't provision credits.");
  }
}

/** Voids a FINALIZED invoice. confirmNumber must equal the invoice number (server re-checks the
 *  type-to-confirm deterrent). */
export async function voidInvoice(
  groupId: number,
  invoiceId: number,
  confirmNumber: string,
  reason?: string | null,
): Promise<void> {
  try {
    await apiClient.post(`${base(groupId)}/${invoiceId}/void`, { confirmNumber, reason });
  } catch (error) {
    rethrowApiError(error, "Couldn't void the invoice.");
  }
}

/** The invoice's activity log (newest first). */
export async function fetchInvoiceEvents(
  groupId: number,
  invoiceId: number,
): Promise<InvoiceEvent[]> {
  const { data } = await apiClient.get<{ success: boolean; data: InvoiceEvent[] }>(
    `${base(groupId)}/${invoiceId}/events`,
  );
  return data.data;
}

/** GFG Finance cross-org blotter — server-paginated, with org/group/status filters + search. */
export async function fetchFinanceInvoices(
  query: InvoiceListQuery = {},
): Promise<InvoiceListResponse> {
  const { data } = await apiClient.get<{ success: boolean } & InvoiceListResponse>(
    "/admin/finance/invoices",
    { params: cleanQuery(query) },
  );
  return data;
}

/** Server-paginated query for the Finance Refunds blotter. */
export interface RefundListQuery {
  search?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  page?: number;
  limit?: number;
}

export interface RefundListResponse {
  data: Refund[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/** GFG Finance cross-org refunds feed — server-paginated, search over reference/reason/invoice #. */
export async function fetchFinanceRefunds(
  query: RefundListQuery = {},
): Promise<RefundListResponse> {
  const { data } = await apiClient.get<{ success: boolean } & RefundListResponse>(
    "/admin/finance/refunds",
    { params: cleanQuery(query) },
  );
  return data;
}

/** Record a client's out-of-band refund against a specific payment (bookkeeping only). */
export async function createFinanceRefund(payload: CreateRefundPayload): Promise<void> {
  try {
    await apiClient.post("/admin/finance/refunds", payload);
  } catch (error) {
    rethrowApiError(error, "Couldn't record the refund.");
  }
}

/** Reverse a recorded refund (negative mirror entry + flips the original). */
export async function reverseFinanceRefund(
  refundId: number,
  payload: ReverseRefundPayload,
): Promise<void> {
  try {
    await apiClient.post(`/admin/finance/refunds/${refundId}/reverse`, payload);
  } catch (error) {
    rethrowApiError(error, "Couldn't reverse the refund.");
  }
}

/** An invoice's payments that still have refundable balance (New Refund picker). */
export async function fetchRefundablePayments(invoiceId: number): Promise<RefundablePayment[]> {
  const { data } = await apiClient.get<{ success: boolean; data: RefundablePayment[] }>(
    `/admin/finance/invoices/${invoiceId}/refundable-payments`,
  );
  return data.data;
}

/** The group's money-in feed (Transactions tab) — server-paginated. */
export async function fetchGroupTransactions(
  groupId: number,
  query: ReceiptListQuery = {},
): Promise<ReceiptListResponse> {
  const { data } = await apiClient.get<{ success: boolean } & ReceiptListResponse>(
    `/admin/groups/${groupId}/transactions`,
    { params: cleanQuery(query) },
  );
  return data;
}

/** An organization's money-in feed across its groups — server-paginated, row-scoped by the API. */
export async function fetchOrgTransactions(
  orgId: number,
  query: ReceiptListQuery = {},
): Promise<ReceiptListResponse> {
  const { data } = await apiClient.get<{ success: boolean } & ReceiptListResponse>(
    "/admin/finance/receipts",
    { params: cleanQuery({ ...query, orgId }) },
  );
  return data;
}

/** The Stripe page for one purchases-feed row: a card receipt, or an invoice's hosted page. */
export async function fetchReceiptDocumentUrl(groupId: number, receiptId: number): Promise<string> {
  try {
    const { data } = await apiClient.get<{ success: boolean; data: { url: string } }>(
      `/admin/groups/${groupId}/transactions/${receiptId}/document`,
    );
    return data.data.url;
  } catch (error) {
    rethrowApiError(error, "Couldn't open the receipt");
  }
}

/** Record a payment (full/partial) or write-off against a finalized invoice. Empty payload = pay the
 *  full outstanding balance. */
export async function recordInvoicePayment(
  groupId: number,
  invoiceId: number,
  payload: RecordPaymentPayload,
): Promise<void> {
  try {
    await apiClient.post(`${base(groupId)}/${invoiceId}/payments`, payload);
  } catch (error) {
    rethrowApiError(error, "Couldn't record the payment.");
  }
}

/** Append-only reversal of a receipt recorded in error, with the credit-lot decision (none/hold/reverse). */
export async function reverseInvoiceReceipt(
  groupId: number,
  invoiceId: number,
  receiptId: number,
  reason?: string | null,
  lotAction?: ReverseLotAction,
): Promise<void> {
  try {
    await apiClient.post(`${base(groupId)}/${invoiceId}/receipts/${receiptId}/reverse`, {
      reason,
      lotAction,
    });
  } catch (error) {
    rethrowApiError(error, "Couldn't reverse the receipt.");
  }
}

/** An invoice's payment receipts (newest first by value date). */
export async function fetchInvoiceReceipts(
  groupId: number,
  invoiceId: number,
): Promise<PaymentReceipt[]> {
  const { data } = await apiClient.get<{ success: boolean; data: PaymentReceipt[] }>(
    `${base(groupId)}/${invoiceId}/receipts`,
  );
  return data.data;
}

/**
 * Records that a Stripe payout for an invoice reached GFG's bank, when the webhook never fired.
 * Distinct from recording a payment, which is the CLIENT paying — this is the deposit landing.
 */
export async function recordInvoicePayout(
  invoiceId: number,
  payload: RecordPayoutPayload,
): Promise<void> {
  try {
    await apiClient.post(`/admin/finance/invoices/${invoiceId}/record-payout`, payload);
  } catch (error) {
    rethrowApiError(error, "Couldn't record the payout.");
  }
}
